"use client";

import React from "react";
import { ArrowRight, Lock, Scale, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const indicators = [{
  id: "confidencialidad",
  title: "Secreto profesional",
  detail: "Protocolo estricto de confidencialidad y custodia documental."
}, {
  id: "cobertura",
  title: "Enfoque multidisciplinario",
  detail: "Articulación directa con nuestras divisiones fiscal y gubernamental."
}, {
  id: "resolucion",
  title: "Certeza procesal",
  detail: "Dictámenes técnicos orientados a blindaje preventivo y litigio estratégico."
}];
export default function Action() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1] as const,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };
  return <section id="llamado-a-la-acción" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      {/* Subtle decorative architectural background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-40">
        <div className="border-border/30 absolute -top-32 left-1/2 h-[500px] w-[900px] -translate-x-1/2 rounded-full border border-dashed" />
        <div className="bg-ring/10 absolute -bottom-24 left-1/2 h-64 w-64 -translate-x-1/2 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <motion.div className="mx-auto max-w-3xl text-center" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={containerVariants}>
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="mb-5 inline-flex items-center gap-2 rounded-sm border border-ring/40 bg-ring/10 px-3.5 py-1 text-xs font-semibold tracking-widest text-ring uppercase">
            <Lock className="size-3 text-ring" />
            <span>Reserva legal absoluta</span>
          </motion.div>

          {/* Headline */}
          <motion.h2 variants={itemVariants} className="font-serif text-3xl font-semibold tracking-wide text-foreground uppercase sm:text-4xl lg:text-5xl">
            Evalúe su postura legal y prevenga contingencias
          </motion.h2>

          {/* ASV Gold Hairline anchor motif */}
          <motion.div variants={itemVariants} className="mx-auto my-6 h-[2px] w-12 bg-ring" role="separator" aria-hidden="true" />

          {/* Supporting line */}
          <motion.p variants={itemVariants} className="font-sans text-base text-muted-foreground sm:text-lg">
            Nuestros especialistas en derecho corporativo, fiscal, administrativo y agrario analizan su situación institucional con el rigor técnico y la discreción que su organización exige.
          </motion.p>

          {/* Primary CTA */}
          <motion.div variants={itemVariants} className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="h-12 w-full rounded-sm border border-ring bg-primary px-8 text-sm font-semibold tracking-wider text-primary-foreground uppercase shadow-md transition-colors duration-250 hover:bg-ring hover:text-primary sm:w-auto">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Hablar con un especialista</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </motion.div>

          {/* Motto tri-point anchor */}
          <motion.p variants={itemVariants} className="mt-6 text-xs font-semibold tracking-widest text-muted-foreground/80 uppercase">
            Estrategia <span className="text-ring">·</span> Cumplimiento <span className="text-ring">·</span> Resultados
          </motion.p>
        </motion.div>

        {/* Institutional trust grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-40px"
      }} variants={containerVariants} className="mt-16 grid grid-cols-1 gap-6 border-t border-border/40 pt-12 md:grid-cols-3">
          {indicators.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="flex items-start gap-4 rounded-sm border border-border/40 bg-card/40 p-5 backdrop-blur-xs">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-sm border border-ring/40 bg-ring/10 text-ring">
                {i === 0 && <Lock className="size-5" />}
                {i === 1 && <Scale className="size-5" />}
                {i === 2 && <ShieldCheck className="size-5" />}
              </div>
              <div>
                <h3 className="font-serif text-sm font-semibold tracking-wider text-foreground uppercase">
                  {item.title}
                </h3>
                <p className="mt-1 font-sans text-xs leading-relaxed text-muted-foreground">
                  {item.detail}
                </p>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}